import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Check, Zap, Crown, Building2, GraduationCap, ArrowRight, CreditCard, Loader2 } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { PortalLayout } from "@/components/portal-layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useEntitlements } from "@/hooks/use-entitlements";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { createPayment } from "@/lib/payos.functions";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/billing")({
  component: BillingPage,
  head: () => ({
    meta: [
      { title: "Gói thanh toán & nâng cấp — Ezyclass" },
      { name: "description", content: "Chọn gói Ezyclass phù hợp với quy mô trung tâm: Gia sư, Trung tâm hoặc Trường học. Thanh toán hàng tháng hoặc hàng năm, hỗ trợ PayOS." },
      { property: "og:title", content: "Gói thanh toán & nâng cấp — Ezyclass" },
      { property: "og:description", content: "So sánh gói Gia sư, Trung tâm và Trường học của Ezyclass. Thanh toán hàng tháng hoặc hàng năm qua PayOS." },
      { property: "og:url", content: "https://ezyclass.com/billing" },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://ezyclass.com/billing" }],
  }),
});


const PLANS = [
  {
    id: "tutor", name: "Gia sư", icon: GraduationCap,
    monthly: 50000, yearly: 500000,
    color: "from-amber-500 to-orange-500",
    features: ["1 lớp", "10 học viên", "Điểm danh + Lịch dạy", "Giao bài tập", "Admin = Giáo viên"],
  },
  {
    id: "center", name: "Trung tâm", icon: Building2,
    monthly: 199000, yearly: 2000000,
    color: "from-blue-600 to-indigo-600",
    badge: "Phổ biến nhất",
    features: ["10 lớp", "100 học viên", "Nhiều giáo viên", "Dashboard + Báo cáo", "Quản lý học phí + lương", "Thông báo hàng loạt", "Xuất Excel"],
  },
  {
    id: "school", name: "Trường học", icon: Crown,
    monthly: 299000, yearly: 3000000,
    color: "from-emerald-600 to-teal-600",
    features: ["Không giới hạn lớp + HS + GV", "Tất cả tính năng Trung tâm", "Báo cáo nâng cao", "Xuất PDF", "Hỗ trợ triển khai"],
  },
];

const PLAN_LABELS: Record<string, string> = {
  free: "Miễn phí", tutor: "Gia sư", center: "Trung tâm",
  school: "Trường học", enterprise: "Enterprise",
};

const tierOrder = ["free", "tutor", "center", "school", "enterprise"];

function BillingPage() {
  const ent = useEntitlements();
  const { user } = useAuth();
  const createPaymentFn = useServerFn(createPayment);
  const [cycle, setCycle] = React.useState<"monthly" | "yearly">("yearly");
  const [loadingPlan, setLoadingPlan] = React.useState<string | null>(null);

  const paymentsQ = useQuery({
    queryKey: ["my-payments", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from("payments")
        .select("id, order_code, amount, plan, billing_cycle, status, created_at, paid_at")
        .order("created_at", { ascending: false }).limit(20);
      return data ?? [];
    },
  });

  const subQ = useQuery({
    queryKey: ["my-subscription", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from("subscriptions")
        .select("*").eq("user_id", user!.id).maybeSingle();
      return data;
    },
  });

  const handleUpgrade = async (planId: string) => {
    setLoadingPlan(planId);
    try {
      const result = await createPaymentFn({ data: { plan: planId as any, cycle } });
      if (result.checkoutUrl) window.location.href = result.checkoutUrl;
    } catch (err: any) {
      toast.error(err?.message ?? "Không tạo được thanh toán");
    } finally {
      setLoadingPlan(null);
    }
  };

  const currentTierIndex = tierOrder.indexOf(ent.tier);

  return (
    <PortalLayout portal="admin" title="Gói & Thanh toán"
      breadcrumb={[{ label: "Admin", to: "/admin" }, { label: "Gói thanh toán" }]}>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="bg-card border rounded-2xl p-6 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Gói hiện tại</div>
            <div className="flex items-center gap-3">
              <span className="font-display text-2xl font-semibold">{PLAN_LABELS[ent.tier]}</span>
              {subQ.data?.status === "active" && (
                <Badge className="bg-emerald-100 text-emerald-700 dark:text-emerald-300">Đang hoạt động</Badge>
              )}
              {ent.tier === "free" && <Badge variant="outline">Giới hạn</Badge>}
            </div>
            {subQ.data?.current_period_end && (
              <div className="text-sm text-muted-foreground mt-1">
                Hết hạn: {new Date(subQ.data.current_period_end).toLocaleDateString("vi-VN")}
              </div>
            )}
          </div>
          <div className="flex gap-6 text-center">
            <div>
              <div className="font-display text-2xl font-semibold">{ent.currentClasses}</div>
              <div className="text-xs text-muted-foreground">/ {ent.maxClasses === -1 ? "∞" : ent.maxClasses} lớp</div>
            </div>
            <div>
              <div className="font-display text-2xl font-semibold">{ent.currentStudents}</div>
              <div className="text-xs text-muted-foreground">/ {ent.maxStudents === -1 ? "∞" : ent.maxStudents} HS</div>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="font-display text-xl font-semibold">Chọn gói phù hợp</h2>
            <p className="text-sm text-muted-foreground mt-1">Nâng cấp ngay, không cần thoát ra ngoài.</p>
          </div>
          <div className="flex items-center gap-1 bg-muted rounded-full p-1 shrink-0">
            <button onClick={() => setCycle("monthly")}
              className={cn("px-4 py-1.5 rounded-full text-sm font-medium transition-all",
                cycle === "monthly" ? "bg-white dark:bg-slate-900 text-foreground shadow-sm" : "text-muted-foreground")}>
              Theo tháng
            </button>
            <button onClick={() => setCycle("yearly")}
              className={cn("px-4 py-1.5 rounded-full text-sm font-medium transition-all",
                cycle === "yearly" ? "bg-white dark:bg-slate-900 text-foreground shadow-sm" : "text-muted-foreground")}>
              Theo năm <span className="ml-1 text-[10px] text-emerald-600 font-semibold">Tiết kiệm</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {PLANS.map((p, i) => {
            const Icon = p.icon;
            const isCurrent = p.id === ent.tier;
            const isDowngrade = tierOrder.indexOf(p.id) <= currentTierIndex;
            const price = cycle === "monthly" ? p.monthly : p.yearly;
            const priceLabel = cycle === "monthly" ? "/tháng" : "/năm";

            return (
              <motion.div key={p.id}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className={cn(
                  "relative bg-card border rounded-2xl p-6 flex flex-col transition-shadow hover:shadow-lg",
                  isCurrent && "ring-2 ring-blue-500",
                  p.badge && "ring-1 ring-blue-200",
                )}>
                {p.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-blue-600 text-white text-[10px] px-3">{p.badge}</Badge>
                  </div>
                )}

                <div className={`size-10 rounded-xl bg-gradient-to-br ${p.color} grid place-items-center mb-4`}>
                  <Icon className="size-5 text-white" />
                </div>

                <h3 className="font-display text-lg font-semibold">{p.name}</h3>

                <div className="mt-3 mb-4">
                  <span className="font-display text-3xl font-bold">
                    {new Intl.NumberFormat("vi-VN").format(price)}đ
                  </span>
                  <span className="text-sm text-muted-foreground">{priceLabel}</span>
                </div>

                <ul className="space-y-2 mb-6 flex-1">
                  {p.features.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-2 text-sm">
                      <Check className="size-4 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {isCurrent ? (
                  <Button disabled className="w-full" variant="outline">Đang sử dụng</Button>
                ) : isDowngrade && ent.tier !== "free" ? (
                  <Button disabled variant="outline" className="w-full opacity-50">Gói thấp hơn</Button>
                ) : (
                  <Button onClick={() => handleUpgrade(p.id)} disabled={!!loadingPlan}
                    className={`w-full bg-gradient-to-r ${p.color} text-white border-0 hover:opacity-90`}>
                    {loadingPlan === p.id ? (
                      <><Loader2 className="size-4 animate-spin mr-1" /> Đang tạo...</>
                    ) : (
                      <><Zap className="size-4 mr-1" /> Nâng cấp</>
                    )}
                  </Button>
                )}
              </motion.div>
            );
          })}
        </div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="mt-5 bg-slate-900 text-white rounded-2xl p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="font-display text-lg font-semibold">Enterprise</h3>
            <p className="text-slate-400 dark:text-slate-500 text-sm mt-1">
              Giải pháp riêng cho hệ thống giáo dục lớn. Tuỳ chỉnh giao diện, API tích hợp, account manager riêng.
            </p>
          </div>
          <a href="mailto:contact@ezyclass.vn?subject=Tư vấn gói Enterprise"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm font-medium hover:bg-slate-100 dark:bg-slate-800 transition shrink-0">
            Liên hệ tư vấn <ArrowRight className="size-4" />
          </a>
        </motion.div>
      </div>

      <div>
        <h2 className="font-display text-xl font-semibold mb-4">Lịch sử thanh toán</h2>
        {(paymentsQ.data ?? []).length === 0 ? (
          <div className="text-center py-10 bg-card border rounded-2xl">
            <CreditCard className="size-8 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Chưa có giao dịch nào.</p>
          </div>
        ) : (
          <div className="bg-card border rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="text-left p-3.5 font-medium">Ngày</th>
                  <th className="text-left p-3.5 font-medium">Gói</th>
                  <th className="text-right p-3.5 font-medium">Số tiền</th>
                  <th className="text-center p-3.5 font-medium">Trạng thái</th>
                </tr>
              </thead>
              <tbody>
                {(paymentsQ.data ?? []).map(p => (
                  <tr key={p.id} className="border-t hover:bg-muted/30 transition-colors">
                    <td className="p-3.5">{new Date(p.created_at).toLocaleDateString("vi-VN")}</td>
                    <td className="p-3.5">
                      {PLAN_LABELS[p.plan] ?? p.plan}
                      <span className="text-xs text-muted-foreground ml-1">
                        ({p.billing_cycle === "monthly" ? "Tháng" : "Năm"})
                      </span>
                    </td>
                    <td className="p-3.5 text-right font-medium">
                      {new Intl.NumberFormat("vi-VN").format(p.amount)}đ
                    </td>
                    <td className="p-3.5 text-center">
                      {p.status === "paid" ? (
                        <Badge className="bg-emerald-100 text-emerald-700 dark:text-emerald-300 text-[10px]">Đã thanh toán</Badge>
                      ) : p.status === "pending" ? (
                        <Badge className="bg-amber-100 text-amber-700 dark:text-amber-300 text-[10px]">Chờ thanh toán</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">Đã huỷ</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
